import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { EditorContent, Node, mergeAttributes, useEditor, useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useTranslation } from "react-i18next";
import {
  CHAT_FILE_TYPES, CHAT_MAX_FILE_BYTES, CHAT_MAX_FILES, CHAT_MAX_FILES_BYTES, CHAT_REACTIONS,
} from "../../api/chat";
import type { ChatPerson } from "../../api/chat";
import { usePrompt } from "../../components/ConfirmDialog";
import Ic from "../../components/icons.jsx";
import { formatBytes } from "../../lib/format";
import { ChatAvatar } from "./ChatAvatar";
import { bodyToDoc, docToBody } from "./chatDoc";
import { matchPeople, mentionQuery } from "./chatFormat";
import { roleLabel } from "./chatPeople";

/** The emoji the writing box offers to put into the text: the reactions and a few more for a sentence. */
const TEXT_EMOJI = [...CHAT_REACTIONS, "🙂", "😉", "🤝", "📦", "🚚", "⚠️"];

/**
 * A colleague named in the text: a chip in the box, `<@id>` in the stored message (see chatDoc), so a rename later
 * does not break it.
 */
const Mention = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-mention"),
        renderHTML: (attrs) => ({ "data-mention": attrs.id }),
      },
      label: { default: "", parseHTML: (el) => (el.textContent ?? "").replace(/^@/, ""), renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-mention]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "chat-mention" }), `@${node.attrs.label}`];
  },
  renderText({ node }) {
    return `@${node.attrs.label}`;
  },
});

type MentionAt = { query: string; from: number; to: number };

/** A picked picture's thumbnail; an icon stands in where the browser cannot make one. */
function previewOf(f: File): string | null {
  if (!f.type.startsWith("image/")) return null;
  try {
    return URL.createObjectURL(f);
  } catch {
    return null;
  }
}

/**
 * The box a message is written in, formatted as it is written, for a new message and for a correction alike.
 *
 * <p>A toolbar and the usual shortcuts (Ctrl/Cmd+B, I, K…) format the text; "@" names a colleague (arrows and Enter
 * pick one); Enter sends, except in a list or a code block, where it goes on writing, and Ctrl/Cmd+Enter always
 * sends; Shift+Enter breaks the line; Escape cancels a correction; Arrow Up in an empty box corrects the last message
 * sent. Pictures and PDFs come in through the paper clip, by pasting or by dropping them on the box.
 */
export function MessageInput({
  variant,
  people,
  placeholder,
  initialBody,
  onSubmit,
  onCancel,
  onDraft,
  onEditLast,
  focusOnOpen,
  incoming,
}: {
  variant: "compose" | "edit";
  people: ChatPerson[];
  placeholder?: string;
  /** The text to start from, as stored (a draft, or the message being corrected). */
  initialBody?: string;
  /** Gets the text as the server stores it, and the files picked (always none for a correction). */
  onSubmit: (body: string, files: File[]) => void;
  onCancel?: () => void;
  onDraft?: (body: string) => void;
  onEditLast?: () => void;
  focusOnOpen?: boolean;
  /** Files dropped on the conversation rather than on the box; a new `id` adds them. */
  incoming?: { id: number; files: File[] };
}) {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const withFiles = variant === "compose";
  const [mention, setMention] = useState<MentionAt | null>(null);
  const [active, setActive] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const matches = mention ? matchPeople(people, mention.query) : [];

  // The editor is made once; what its handlers need of this render they read through refs.
  const keyDown = useRef<(e: KeyboardEvent) => boolean>(() => false);
  const takeFiles = useRef<(list: File[]) => void>(() => {});
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
        underline: false,
        trailingNode: false,
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: "https",
          isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && !/^(javascript|data|vbscript|file):/i.test(url),
        },
      }),
      Mention,
    ],
    content: initialBody ? bodyToDoc(initialBody, peopleById) : undefined,
    editorProps: {
      attributes: {
        class: "chat-editor-content",
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": placeholder ?? t("chat.message", "Message"),
      },
      handleKeyDown: (_view, event) => keyDown.current(event),
      handlePaste: (_view, event) => {
        const list = Array.from(event.clipboardData?.files ?? []);
        if (!withFiles || list.length === 0) return false;
        takeFiles.current(list);
        return true;
      },
      handleDrop: (_view, event) => {
        const list = Array.from((event as DragEvent).dataTransfer?.files ?? []);
        if (!withFiles || list.length === 0) return false;
        takeFiles.current(list);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      findMention(e);
      onDraftRef.current?.(docToBody(e.getJSON()));
    },
    onSelectionUpdate: ({ editor: e }) => findMention(e),
  });

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => e ? {
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      strike: e.isActive("strike"),
      link: e.isActive("link"),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      quote: e.isActive("blockquote"),
      code: e.isActive("code"),
      codeBlock: e.isActive("codeBlock"),
      empty: e.isEmpty,
    } : null,
  });

  useEffect(() => {
    if (focusOnOpen && editor) editor.commands.focus("end");
  }, [focusOnOpen, editor]);

  useEffect(() => {
    if (incoming && incoming.files.length > 0) takeFiles.current(incoming.files);
  }, [incoming]);

  const previews = useMemo(() => files.map(previewOf), [files]);
  useEffect(() => () => previews.forEach((u) => {
    try {
      if (u) URL.revokeObjectURL(u);
    } catch {
      // Nothing to free.
    }
  }), [previews]);

  /** The "@name" being written right before the caret, if any. */
  function findMention(e: Editor) {
    const { $from, empty } = e.state.selection;
    if (!empty) {
      setMention(null);
      return;
    }
    const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 40), $from.parentOffset, "\n", "￼");
    const query = mentionQuery(before);
    setMention(query === null ? null : { query, from: $from.pos - query.length - 1, to: $from.pos });
    setActive(0);
  }

  function pick(person: ChatPerson) {
    if (!editor || !mention) return;
    editor.chain().focus().insertContentAt({ from: mention.from, to: mention.to }, [
      { type: "mention", attrs: { id: person.id, label: person.name } },
      { type: "text", text: " " },
    ]).run();
    setMention(null);
  }

  function addFiles(list: File[]) {
    setProblem(null);
    const next = [...files];
    for (const f of list) {
      if (!CHAT_FILE_TYPES.includes(f.type)) {
        setProblem(t("chat.fileType", "Pictures (PNG, JPEG, WebP, GIF) and PDF only."));
      } else if (f.size > CHAT_MAX_FILE_BYTES) {
        setProblem(t("chat.fileTooLarge", "A file can have up to 20 MB."));
      } else if (next.length >= CHAT_MAX_FILES) {
        setProblem(t("chat.tooManyFiles", "Up to 5 files a message."));
        break;
      } else if (next.reduce((sum, x) => sum + x.size, 0) + f.size > CHAT_MAX_FILES_BYTES) {
        setProblem(t("chat.filesTooLarge", "A message can carry up to 45 MB."));
        break;
      } else {
        next.push(f);
      }
    }
    setFiles(next);
    editor?.commands.focus();
  }
  takeFiles.current = addFiles;

  function submit() {
    if (!editor) return;
    const body = docToBody(editor.getJSON());
    if (!body && files.length === 0) return;
    onSubmit(body, files);
    if (variant === "compose") {
      editor.commands.clearContent(true);
      setFiles([]);
      setProblem(null);
    }
    setMention(null);
  }

  async function addLink() {
    if (!editor) return;
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = await prompt({
      title: t("chat.linkTitle", "Link"),
      message: t("chat.linkPrompt", "The address, starting with https://"),
      defaultValue: previous ?? "https://",
    });
    if (url === null) return;
    const href = url.trim();
    if (!href || href === "https://") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    if (!/^https?:\/\/\S+$/i.test(href)) {
      setProblem(t("chat.linkInvalid", "A link starts with https:// or http://."));
      return;
    }
    if (editor.state.selection.empty && !editor.isActive("link")) {
      // Nothing selected: the address is the link's text too.
      editor.chain().focus().insertContent([
        { type: "text", text: href, marks: [{ type: "link", attrs: { href } }] },
        { type: "text", text: " " },
      ]).run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }
  }

  keyDown.current = (e) => {
    if (e.isComposing || !editor) return false;
    if (mention && matches.length > 0) {
      if (e.key === "ArrowDown") { setActive((i) => (i + 1) % matches.length); return true; }
      if (e.key === "ArrowUp") { setActive((i) => (i - 1 + matches.length) % matches.length); return true; }
      if (e.key === "Enter" || e.key === "Tab") { pick(matches[Math.min(active, matches.length - 1)]); return true; }
      if (e.key === "Escape") { setMention(null); return true; }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { submit(); return true; }
    if (e.key === "Enter" && !e.shiftKey) {
      // In a list or a code block Enter goes on writing; elsewhere it sends.
      if (editor.isActive("codeBlock") || editor.isActive("listItem")) return false;
      submit();
      return true;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { void addLink(); return true; }
    if (e.key === "Escape" && onCancel) { onCancel(); return true; }
    if (e.key === "ArrowUp" && onEditLast && editor.isEmpty && files.length === 0) { onEditLast(); return true; }
    return false;
  };

  function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (list.length > 0) addFiles(list);
  }

  function tool(label: string, icon: ReactNode, pressed: boolean | undefined, run: () => void, shortcut?: string) {
    return (
      <button
        type="button"
        className="chat-tool"
        aria-pressed={pressed}
        aria-label={label}
        title={shortcut ? `${label} (${shortcut})` : label}
        onMouseDown={(e) => e.preventDefault()}
        onClick={run}
      >
        {icon}
      </button>
    );
  }

  const mod = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl+";
  const chain = () => editor!.chain().focus();
  const canSend = !!state && (!state.empty || files.length > 0);

  return (
    <div className={`chat-input ${variant}`}>
      {mention && matches.length > 0 && (
        <div className="chat-picker" role="listbox" aria-label={t("chat.mentionPicker", "Colleagues")}>
          {matches.map((p, i) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`chat-picker-row${i === active ? " active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setActive(i)}
            >
              <ChatAvatar person={p} id={p.id} size="sm" online={p.online} />
              <span className="chat-picker-name">{p.name}</span>
              <small>{roleLabel(p, t)}</small>
            </button>
          ))}
        </div>
      )}
      <div className="chat-box">
        {editor && state && (
          <div className="chat-format" role="toolbar" aria-label={t("chat.formatting", "Formatting")}>
            {tool(t("chat.fmtBold", "Bold"), <Ic.bold />, state.bold, () => chain().toggleBold().run(), `${mod}B`)}
            {tool(t("chat.fmtItalic", "Italic"), <Ic.italic />, state.italic, () => chain().toggleItalic().run(), `${mod}I`)}
            {tool(t("chat.fmtStrike", "Strikethrough"), <Ic.strike />, state.strike, () => chain().toggleStrike().run(), `${mod}Shift+S`)}
            {tool(t("chat.fmtLink", "Link"), <Ic.link />, state.link, () => void addLink(), `${mod}K`)}
            <span className="chat-format-sep" aria-hidden="true" />
            {tool(t("chat.fmtBullets", "Bulleted list"), <Ic.listBullet />, state.bullet, () => chain().toggleBulletList().run(), `${mod}Shift+8`)}
            {tool(t("chat.fmtNumbers", "Numbered list"), <Ic.listNumbered />, state.ordered, () => chain().toggleOrderedList().run(), `${mod}Shift+7`)}
            {tool(t("chat.fmtQuote", "Quote"), <Ic.quote />, state.quote, () => chain().toggleBlockquote().run(), `${mod}Shift+B`)}
            <span className="chat-format-sep" aria-hidden="true" />
            {tool(t("chat.fmtCode", "Code"), <Ic.code />, state.code, () => chain().toggleCode().run(), `${mod}E`)}
            {tool(t("chat.fmtCodeBlock", "Code block"), <Ic.codeBlock />, state.codeBlock, () => chain().toggleCodeBlock().run())}
          </div>
        )}
        {files.length > 0 && (
          <div className="chat-pick">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="chat-pick-file">
                {previews[i] ? <img src={previews[i]!} alt="" /> : <span className="chat-pick-icon"><Ic.pdf /></span>}
                <span className="chat-pick-meta">
                  <b>{f.name}</b>
                  <small>{formatBytes(f.size)}</small>
                </span>
                <button
                  type="button"
                  aria-label={t("chat.removeFile", "Remove {{name}}", { name: f.name })}
                  onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}
                >
                  <Ic.x />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="chat-editor">
          {state?.empty && files.length === 0 && placeholder && (
            <div className="chat-editor-placeholder" aria-hidden="true">{placeholder}</div>
          )}
          <EditorContent editor={editor} />
        </div>
        <div className="chat-box-bar">
          {withFiles && (
            <>
              <button
                type="button"
                className="chat-tool"
                title={t("chat.attach", "Attach pictures or PDF")}
                aria-label={t("chat.attach", "Attach pictures or PDF")}
                onClick={() => picker.current?.click()}
              >
                <Ic.attach />
              </button>
              <input ref={picker} type="file" multiple accept={CHAT_FILE_TYPES.join(",")} onChange={onPicked} hidden />
            </>
          )}
          <button
            type="button"
            className="chat-tool"
            title={t("chat.mention", "Mention a colleague")}
            aria-label={t("chat.mention", "Mention a colleague")}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!editor) return;
              const { $from } = editor.state.selection;
              const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 1), $from.parentOffset, "\n", "￼");
              chain().insertContent(before && !/\s/.test(before) ? " @" : "@").run();
            }}
          >
            <Ic.at />
          </button>
          <span className="chat-emoji-anchor">
            <button
              type="button"
              className="chat-tool"
              title={t("chat.emoji", "Emoji")}
              aria-label={t("chat.emoji", "Emoji")}
              aria-expanded={emojiOpen}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setEmojiOpen((o) => !o)}
            >
              <Ic.smile />
            </button>
            {emojiOpen && (
              <div className="chat-palette up" role="menu">
                {TEXT_EMOJI.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    role="menuitem"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      chain().insertContent(emoji).run();
                      setEmojiOpen(false);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </span>
          {problem && <span className="chat-input-problem" role="alert">{problem}</span>}
          {variant === "edit" ? (
            <span className="chat-edit-actions">
              <button type="button" className="btn sm" onClick={onCancel}>{t("chat.cancel", "Cancel")}</button>
              <button type="button" className="btn sm primary" onClick={submit}>{t("chat.save", "Save")}</button>
            </span>
          ) : (
            <button
              type="button"
              className="chat-send"
              onClick={submit}
              disabled={!canSend}
              title={t("chat.send", "Send")}
              aria-label={t("chat.send", "Send")}
            >
              <Ic.send />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
